// ABOUTME: Modal for bulk-assigning a set of support tickets to a warehouse user.
// ABOUTME: Triggered from the Unassigned tab's selected-action bar; calls bulkAssign API.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import cx from 'classnames';

import SHModal from "../../../../../../components/theme/Modal/SHModal";
import ButtonGroup from "../../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import { ShDropdownQuery } from "../../../../../../components/theme/dropdowns/Dropdown";
import Subheading from "../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../components/theme/text/Text";

import { useGetUsersSelectQuery } from "../../../../../../newapi/user/userSlice";
import { useBulkAssignSupportTicketsMutation } from "../../../../../../newapi/warehouse/supportTicketApiSlice";
import { tokens } from "../../../../../../theme";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../../../../utils/toasts";

// Assign-to targets: only servicedesk-capable users. The named filter maps to
// its privilege set server-side; admins always pass regardless of privileges.
const ASSIGNEE_FILTER = { filter: "servicedesk_assignee" };

const UserOption = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ButtonBase
            key={props.key}
            onClick={props.onClick}
            className={cx('', { 'active': props.isSelected })}
            sx={{
                flexShrink: 0,
                height: 32,
                minWidth: 300,
                width: 1,
                pl: 1.5,
                pr: 0.5,
                textAlign: "left",
                display: "flex",
                justifyContent: 'flex-start',
                alignItems: "center",
                borderRadius: 2,
                '&.active': {
                    background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                    color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                    fontWeight: "900",
                },
            }}
        >
            {props.option.name}
        </ButtonBase>
    );
};

const MBulkAssignSupportTickets = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedUser, setSelectedUser] = useState({ id: -1, name: t("no_selection") });
    const [userError, setUserError] = useState(null);

    const [bulkAssign, { isLoading }] = useBulkAssignSupportTicketsMutation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleClose = () => {
        setSelectedUser({ id: -1, name: t("no_selection") });
        setUserError(null);
        props.handleClose();
    };

    const handleConfirm = () => {
        if (isLoading) return;
        setUserError(null);

        if (selectedUser.id === -1) {
            setUserError(t("user_required"));
            return;
        }

        bulkAssign({
            ticketIds: props.selectedItems,
            assignedToId: selectedUser.id,
        })
            .unwrap()
            .then((res) => {
                showToastMessagePostRequest(t, res);
                props.setSelectedItems?.([]);
                handleClose();
            })
            .catch((err) => {
                showToastMessageRequestError(t("bulk_assign_failed"), t, err);
            });
    };

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
            sx={{ body: { width: 450 } }}
        >
            <Box
                sx={{
                    paddingY: 3,
                    paddingX: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Subheading textAlign="center">{t("assign_to_user")}</Subheading>
                <Box
                    sx={{
                        mt: 2,
                        borderRadius: 5,
                        height: 40,
                        display: 'flex',
                        alignItems: "center",
                        justifyContent: 'space-between',
                        pl: 2,
                        pr: 0.5,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                    }}
                >
                    <Text>{t("amount_of_tickets_selected")}</Text>
                    <Box
                        sx={{
                            height: 32,
                            width: 32,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: "center",
                            justifyContent: 'center',
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        }}
                    >
                        <Text bold>{props.selectedItems?.length ?? 0}</Text>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    pb: 1,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ShDropdownQuery
                    name="name"
                    onChange={(selected, object) => {
                        setSelectedUser(
                            selected !== null
                                ? object
                                : { id: -1, name: t("no_selection") }
                        );
                    }}
                    valueKey={"id"}
                    dataKey={null}
                    shComponent={<UserOption />}
                    selected={selectedUser.id}
                    content={useGetUsersSelectQuery}
                    config={ASSIGNEE_FILTER}
                    closeOnSelection
                >
                    {({ onClick }) => (
                        <ButtonBase
                            onClick={onClick}
                            sx={{
                                height: 32,
                                borderRadius: 5,
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                display: "flex",
                                alignItems: 'center',
                                px: 0.5,
                                position: 'relative',
                                "&:before": {
                                    content: '""',
                                    position: 'absolute',
                                    borderRadius: 5,
                                    left: 0,
                                    top: 0,
                                    width: 1,
                                    height: 1,
                                    background: userError ? colors.red[100] : theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    height: 24,
                                    lineHeight: "24px",
                                    background: userError ? colors.red[400] : theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    borderRadius: 3,
                                    border: `1px solid ${userError ? colors.red[400] : theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                    width: 110,
                                    textAlign: "center",
                                    fontSize: 14,
                                    fontWeight: "bold",
                                    color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                }}
                            >
                                {t("user")}
                            </Box>
                            <Box sx={{ flex: 1, ml: 1.5, textAlign: "left" }}>
                                <Text bold>{selectedUser?.name}</Text>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Text italic bold variant={userError ? "red" : theme.palette.mode === "dark" ? "blue" : "primary"}>
                                    {t("required")}
                                </Text>
                                <Box
                                    sx={{
                                        height: 24,
                                        width: 24,
                                        borderRadius: 3,
                                        display: "flex",
                                        alignItems: 'center',
                                        justifyContent: "center",
                                        background: userError ? colors.red[100] : theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                        border: `1px solid ${userError ? colors.red[400] : theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                        color: userError ? colors.red[400] : theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    }}
                                >
                                    <KeyboardArrowDownIcon />
                                </Box>
                            </Box>
                        </ButtonBase>
                    )}
                </ShDropdownQuery>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    paddingLeft: 3,
                    paddingTop: 2,
                    zIndex: 1058,
                    position: "relative",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                        paddingLeft: 0,
                    },
                }}
            >
                <Shbutton
                    isCancel
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleConfirm}
                        isLoading={isLoading}
                    >
                        {t("assign")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MBulkAssignSupportTickets;
